import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { NgClass } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { Approval } from '../../types';
import { getStatusText } from '../../../utils/component/state-filter-combobox/state-cb.component';
import { WidgetMessageService } from '../../services/widget-message.service';
import { InvalidateLoadedQueries } from '../../state/entity.state';

export interface HasApprovalState {
  id: string
  approval: Approval
  [key: string]: unknown
}

export interface EntityUpdateService<T> {
  update(entity: T): Observable<unknown>
}

@Component({
  selector: 'pixl-state-badge',
  standalone: true,
  imports: [NgClass, MatMenuModule, MatButtonModule, TranslocoPipe],
  templateUrl: './state-badge.component.html',
  styleUrl: './state-badge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StateBadgeComponent {
  public entity = input.required<HasApprovalState>();
  public entityService = input.required<EntityUpdateService<unknown>>();
  public refresh$ = input.required<BehaviorSubject<void>>();
  public readonly badgeClicked = output<string>();

  public entityClass = input<new () => HasApprovalState>(
    class implements HasApprovalState {
      id = '';
      approval: Approval = 'Dormant';
      [key: string]: unknown;
    }
  );

  private readonly store = inject(Store);
  private readonly widgetService = inject(WidgetMessageService);
  private readonly translocoService = inject(TranslocoService);

  public readonly availableStatuses: Approval[] = ['Dormant', 'Edit', 'Pending', 'Live'];

  public getTranslationKey(approval: Approval): string {
    return getStatusText(approval);
  }

  public getStatusColor(approval: Approval): string {
    return {
      Dormant: 'badge-dormant',
      Edit: 'badge-edit',
      Pending: 'badge-pending',
      Live: 'badge-live',
    }[approval] || '';
  }

  public async changeState(newStatus: Approval): Promise<void> {
    const currentEntity = this.entity();
    if (currentEntity.approval === newStatus) return;

    const updatedEntity = Object.assign(new (this.entityClass())(), currentEntity, { approval: newStatus });

    try {
      await firstValueFrom(this.entityService().update(updatedEntity));
      this.store.dispatch(new InvalidateLoadedQueries());
      this.refresh$().next();

      this.widgetService.setMessage({
        message: this.translocoService.translate('state_saved'),
        messageTye: 'mtSuccess'
      });
    }
    catch (error) {
      console.error('Fehler beim Ändern des Status:', error);
      this.widgetService.setMessage({
        message: this.translocoService.translate('preview.saveError'),
        messageTye: 'mtError'
      });
    }
  }
}
